import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { queryClient } from "./lib/queryClient";
import { TooltipProvider } from "./components/ui/Tooltip";
import { ToastHost } from "./components/ui/Toast";
import { ConfirmHost } from "./components/ui/Dialog";
import { LinkGuardHost } from "./components/LinkGuard";
import { AuthProvider } from "./features/auth/AuthProvider";
import { LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailPage } from "./features/auth/AuthPages";
import { AppShell } from "./app/AppShell";
import { lazy, Suspense, type ReactNode } from "react";
import { NotFoundPage } from "./features/pages/PageHeader";
import { PageSpinner } from "./components/ui/Spinner";
import { RouteBoundary } from "./components/ErrorBoundary";
import { PostDialogsHost } from "./features/posts/PostDialogs";

// Secondary screens load on demand; the shell, feed and communities ship in the main bundle.
const CookiePolicyPage = lazy(() => import("./features/marketing/PolicyPages").then((m) => ({ default: m.CookiePolicyPage })));
const RefundPolicyPage = lazy(() => import("./features/marketing/PolicyPages").then((m) => ({ default: m.RefundPolicyPage })));
const SecurityPage = lazy(() => import("./features/marketing/PolicyPages").then((m) => ({ default: m.SecurityPage })));
const LandingPage = lazy(() => import("./features/marketing/LandingPage").then((m) => ({ default: m.LandingPage })));
const AuthorizePage = lazy(() => import("./features/oauth/AuthorizePage").then((m) => ({ default: m.AuthorizePage })));
const PostPage = lazy(() => import("./features/posts/PostPage").then((m) => ({ default: m.PostPage })));
const ProfilePage = lazy(() => import("./features/users/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const ExplorePage = lazy(() => import("./features/explore/ExplorePage").then((m) => ({ default: m.ExplorePage })));
const TopicPage = lazy(() => import("./features/explore/ExplorePage").then((m) => ({ default: m.TopicPage })));
const MessagesPage = lazy(() => import("./features/messages/MessagesPage").then((m) => ({ default: m.MessagesPage })));
const NotificationsPage = lazy(() => import("./features/notifications/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const CommunityPage = lazy(() => import("./features/communities/CommunityPage").then((m) => ({ default: m.CommunityPage })));
const InvitePage = lazy(() => import("./features/communities/CommunityPage").then((m) => ({ default: m.InvitePage })));
const SearchPage = lazy(() => import("./features/search/SearchPage").then((m) => ({ default: m.SearchPage })));
const SavedPage = lazy(() => import("./features/saved/SavedPage").then((m) => ({ default: m.SavedPage })));
const FriendsPage = lazy(() => import("./features/relationships/FriendsPage").then((m) => ({ default: m.FriendsPage })));
const SettingsPage = lazy(() => import("./features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const PrivacyPage = lazy(() => import("./features/legal/PrivacyPage").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./features/legal/TermsPage").then((m) => ({ default: m.TermsPage })));
const StaffPage = lazy(() => import("./features/staff/StaffPage").then((m) => ({ default: m.StaffPage })));

/** Old sign-up address; keeps ?next= so invite links still return people to the invite. */
function RedirectToSignUp() {
  const { search } = useLocation();
  return <Navigate to={`/sign-up${search}`} replace />;
}

/** Lazy route: loading fallback plus an error boundary that resets on navigation. */
const L = ({ children }: { children: ReactNode }) => (
  <RouteBoundary>
    <Suspense fallback={<PageSpinner />}>{children}</Suspense>
  </RouteBoundary>
);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TooltipProvider>
          <AuthProvider>
            <Routes>
              {/* The public front page. The app itself starts at /feed. */}
              <Route path="/" element={<L><LandingPage /></L>} />
              <Route path="/cookies" element={<L><CookiePolicyPage /></L>} />
              <Route path="/refunds" element={<L><RefundPolicyPage /></L>} />
              <Route path="/security" element={<L><SecurityPage /></L>} />
              <Route path="/login" element={<RouteBoundary><LoginPage /></RouteBoundary>} />
              <Route path="/sign-up" element={<RouteBoundary><RegisterPage /></RouteBoundary>} />
              <Route path="/register" element={<RedirectToSignUp />} />
              <Route path="/resetpassword" element={<RouteBoundary><ResetPasswordPage /></RouteBoundary>} />
              <Route path="/verify-email" element={<RouteBoundary><VerifyEmailPage /></RouteBoundary>} />
              {/* Legal pages: public, readable signed in or out, outside the app chrome. */}
              <Route path="/privacy" element={<L><PrivacyPage /></L>} />
              <Route path="/terms" element={<L><TermsPage /></L>} />
              {/* Third-party apps and bot install links land here; focused, outside the app chrome. */}
              <Route path="/oauth2/authorize" element={<RouteBoundary><L><AuthorizePage /></L></RouteBoundary>} />
              <Route element={<AppShell />}>
                <Route path="/feed" element={null} />
                <Route path="/c/*" element={null} />
                <Route path="/explore" element={<L><ExplorePage /></L>} />
                <Route path="/t/:topic" element={<L><TopicPage /></L>} />
                <Route path="/messages/:id?" element={<L><MessagesPage /></L>} />
                <Route path="/notifications" element={<L><NotificationsPage /></L>} />
                <Route path="/search" element={<L><SearchPage /></L>} />
                <Route path="/u/:username" element={<L><ProfilePage /></L>} />
                <Route path="/p/:id" element={<L><PostPage /></L>} />
                <Route path="/community/:slug" element={<L><CommunityPage /></L>} />
                <Route path="/invite/:code" element={<L><InvitePage /></L>} />
                <Route path="/saved" element={<L><SavedPage /></L>} />
                <Route path="/friends" element={<L><FriendsPage /></L>} />
                <Route path="/settings/:section?" element={<L><SettingsPage /></L>} />
                <Route path="/staff/:section?/:id?" element={<L><StaffPage /></L>} />
                <Route path="*"element={<RouteBoundary><NotFoundPage /></RouteBoundary>} />
              </Route>
            </Routes>
            <ToastHost />
            <ConfirmHost />
            <LinkGuardHost />
            <PostDialogsHost />
          </AuthProvider>
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
